import { useCallback, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { isVersionConflict } from "models/common";
import { CommonApiResultError } from "shared/networking/common_api_result";
import HttpError from "shared/networking/http_error";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import { useTranslation } from "react-i18next";

const ErrorListener = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const queryUnsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (event.type === "updated" && event.action.type === "error") {
        handleError(event.action.error);
      }
    });

    const mutationUnsubscribe = queryClient
      .getMutationCache()
      .subscribe((event) => {
        if (event.type === "updated" && event.action.type === "error") {
          handleError(event.action.error);
        }
      });

    return () => {
      queryUnsubscribe();
      mutationUnsubscribe();
    };
  }, [queryClient]);

  const handleError = (error: Error) => {
    if (error instanceof HttpError && error.code === "401") {
      const returnUrl = encodeURIComponent(window.location.pathname);
      document.location.href = `/account/login?returnUrl=${returnUrl}`;
      return;
    }

    if (error instanceof CommonApiResultError && error.errorCode === 401) {
      const returnUrl = encodeURIComponent(window.location.pathname);
      document.location.href = `/account/login?returnUrl=${returnUrl}`;
      return;
    }

    if (error instanceof CommonApiResultError) {
      if (isVersionConflict(error)) {
        setErrorMessage(
          t("Error_VersionConflict", {
            defaultValue:
              "Someone else changed this entry while you were editing it. Reload the page to see the current state, then apply your changes again.",
          }),
        );
        return;
      }

      const translatedErrorMessage = resolveMpiErrorMessage(error);
      let message = translatedErrorMessage + "\n";
      message += formatErrors(error.messageParameter);
      setErrorMessage(message);
    } else {
      setErrorMessage(
        `An unexpected error occurred.\nDetails: ${error.message}`,
      );
    }
  };

  const handleClose = useCallback(() => {
    setErrorMessage(null);
  }, []);

  const formatErrors = (obj: any) => {
    if (!obj || typeof obj !== "object") return "";

    return Object.entries(obj)
      .map(([key, value]) => `${key}: ${String(value)}`)
      .join("\n");
  };

  const resolveMpiErrorMessage = (error: CommonApiResultError): string => {
    const normalizedSubCode = error.errorSubCode?.replace(/\D/g, "");
    if (!normalizedSubCode) {
      return error.message;
    }

    const translationKey = `MPI_MDS_${normalizedSubCode.padStart(8, "0")}`;
    const translated = t(translationKey);
    return translated === translationKey ? error.message : translated;
  };

  if (!errorMessage) return null;

  return (
    <Dialog open={true} onClose={handleClose} disableEscapeKeyDown={true}>
      <DialogTitle>{t("Error")}</DialogTitle>
      <DialogContent>
        {errorMessage.split("\n").map((line, index) => (
          <div key={index}>{line}</div>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary" autoFocus>
          {t("Button_Close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ErrorListener;
